import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiUsers, FiSearch, FiMessageSquare, FiX, FiEdit } from 'react-icons/fi';
import SummaryApi from '../common';
import Modal from 'react-modal';

// ================= THEME (UI rules) =================
const THEME = {
  primary: 'rgb(23, 44, 87)', // #172C57 — main brand color
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionYellow: '#D97706',
  actionYellowHover: '#B45309',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionPurple: '#7C3AED',
  actionPurpleHover: '#6D28D9',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
  disabledGray: '#D1D5DB',
};

const EmployeePromotionDataReport = () => {
  // State
  const [promotionData, setPromotionData] = useState([]);
  const [filteredPromotionData, setFilteredPromotionData] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedFeedback, setExpandedFeedback] = useState({});
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [readCount, setReadCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [feedbackModalIsOpen, setFeedbackModalIsOpen] = useState(false);
  const [selectedFeedbackStudent, setSelectedFeedbackStudent] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  // Helper Functions
  const normalizeStatus = (status) => {
    if (!status) return 'N/A';
    const lower = status.toLowerCase();
    if (lower === 'delivered') return 'Delivered';
    if (lower === 'read') return 'Read';
    return status;
  };

  // Semantic status colors: Delivered = green, Read = yellow, Pending/Neutral = gray
  const getStatusStyle = (status) => {
    if (!status) return 'bg-gray-100 text-gray-600';
    const lower = status.toLowerCase();
    if (lower === 'delivered') return 'bg-green-100 text-green-700';
    if (lower === 'read') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  const fetchMemberType = () => {
    const encodedEmpId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
    if (encodedEmpId) {
      setEmployeeId(atob(encodedEmpId));
    }
  };

  useEffect(() => {
    fetchMemberType();
  }, []);

  useEffect(() => {
    if (employeeId) {
      fetchPromotionData();
    }
  }, [employeeId, dateFilter, selectedDate]);

  const getFilterDate = () => {
    if (dateFilter === 'today') {
      return new Date().toISOString().split('T')[0];
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    } else if (dateFilter === 'custom' && selectedDate) {
      return selectedDate;
    }
    return null;
  };

  const calculateStatusCounts = (data) => {
    let delivered = 0, read = 0;
    data.forEach(item => {
      const lower = item.status?.toLowerCase();
      if (lower === 'delivered') delivered++;
      else if (lower === 'read') read++;
    });
    setDeliveredCount(delivered);
    setReadCount(read);
  };

  const fetchPromotionData = async () => {
    if (!employeeId) {
      toast.error('Employee ID not found');
      return;
    }

    setLoading(true);
    try {
      const filterDate = getFilterDate();
      if (!filterDate) {
        toast.warning('Please select a valid date');
        setLoading(false);
        return;
      }

      const response = await fetch(SummaryApi.getPromotionStudentExcelData.url, {
        method: SummaryApi.getPromotionStudentExcelData.method,
        headers,
        body: JSON.stringify({
          employee_id: employeeId,
          promotion_date: filterDate
        }),
      });

      const result = await response.json();

      if (result.status && result.data) {
        const formattedData = result.data.map((item, index) => ({
          id: index,
          promotion_data_id: item.promotion_data_id,
          student_name: item.student_name || 'N/A',
          mobile_number: item.mobile_number || 'N/A',
          status: item.status || 'Pending',
          promotion_date: item.promotion_date,
          created_date: item.created_date,
          feedback: item.feedback || null,
          promotion_details: item.promotion_details || []
        }));

        setPromotionData(formattedData);
        setFilteredPromotionData(formattedData);
        calculateStatusCounts(formattedData);
      } else {
        setPromotionData([]);
        setFilteredPromotionData([]);
        setDeliveredCount(0);
        setReadCount(0);
        toast.info(result.message || 'No data found');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch data');
      setPromotionData([]);
      setFilteredPromotionData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...promotionData];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.student_name?.toLowerCase().includes(term) ||
        item.mobile_number?.includes(term) ||
        item.status?.toLowerCase().includes(term)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(item =>
        item.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredPromotionData(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, promotionData]);

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== 'custom') setSelectedDate('');
    setCurrentPage(1);
  };

  const handleCustomDateChange = (e) => {
    setSelectedDate(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('today');
    setSelectedDate('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
  };

  const openFeedbackModal = (student) => {
    setSelectedFeedbackStudent(student);
    setFeedbackText(student.feedback || '');
    setFeedbackModalIsOpen(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalIsOpen(false);
    setSelectedFeedbackStudent(null);
    setFeedbackText('');
  };

  const updatePromotionFeedback = async () => {
    if (!selectedFeedbackStudent?.promotion_data_id) {
      toast.error('Promotion Data ID not found');
      return;
    }
    if (!feedbackText.trim()) {
      toast.warning('Please enter feedback');
      return;
    }

    setFeedbackLoading(true);
    try {
      const response = await fetch(SummaryApi.updatepromitionfeedback.url, {
        method: SummaryApi.updatepromitionfeedback.method,
        headers: {
          'Authorization': 'Bearer sandeep_university_916',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          promotion_data_id: selectedFeedbackStudent.promotion_data_id,
          feedback: feedbackText.trim()
        }),
      });

      const result = await response.json();
      if (result.status) {
        toast.success('Feedback updated successfully');
        closeFeedbackModal();
        fetchPromotionData();
      } else {
        toast.error(result.message || 'Failed to update');
      }
    } catch (error) {
      toast.error('Error updating feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getFilterDisplayText = () => {
    if (dateFilter === 'today') return 'Today';
    if (dateFilter === 'yesterday') return 'Yesterday';
    if (dateFilter === 'custom' && selectedDate) return formatDate(selectedDate);
    return 'Today';
  };

  const renderStatusBadge = (status) => (
    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(status)}`}>
      {normalizeStatus(status)}
    </span>
  );

  // Updated renderFeedbackCell as per your request
  const renderFeedbackCell = (item) => {
    const hasFeedback = item.feedback && item.feedback.trim() !== '';
    const isExpanded = expandedFeedback[item.promotion_data_id];

    // 👉 If NO feedback (N/A case)
    if (!hasFeedback) {
      return (
        <div
          onClick={() => openFeedbackModal(item)}
          className="flex items-center gap-2 text-gray-500 cursor-pointer hover:text-[#2563EB]"
          title="Add Feedback"
        >
          <span className="font-medium text-sm">N/A</span>

          <FiEdit
            size={18}
            className="shrink-0 text-[#2563EB] cursor-pointer hover:text-[#1D4ED8]"
            onClick={() => openFeedbackModal(item)}
          />
        </div>
      );
    }

    // 👉 If feedback exists
    return (
      <div className="text-sm text-gray-700 text-left max-w-[240px] break-words">
        <p
          onClick={() => openFeedbackModal(item)}
          className={`cursor-pointer hover:text-[#2563EB] ${isExpanded ? 'whitespace-normal break-words' : 'truncate'
            }`}
          title="Click to edit feedback"
        >
          {item.feedback}
          <FiEdit
            size={18}
            onClick={() => openFeedbackModal(item)}
            className="inline-block ml-1 shrink-0 text-[#2563EB] cursor-pointer hover:text-[#1D4ED8]"
          />
        </p>

        {item.feedback.length > 40 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpandedFeedback((prev) => ({
                ...prev,
                [item.promotion_data_id]: !prev[item.promotion_data_id],
              }));
            }}
            className="text-[#2563EB] text-xs mt-1 hover:underline"
          >
            {isExpanded ? 'Show Less' : 'Read More'}
          </button>
        )}
      </div>
    );
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPromotionData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPromotionData.length / itemsPerPage);

  Modal.setAppElement('#root');

  // Shared style helpers
  const inputClass =
    'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition ' +
    'placeholder:text-gray-400 hover:border-gray-400 focus:border-[#172C57] focus:ring-2 focus:ring-[#172C57]';
  const pageBtnClass =
    'rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-semibold text-[#172C57] transition hover:bg-gray-50 ' +
    'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400';

  return (
    <div className="min-h-screen w-full">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-2xl text-center">
            <div
              className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mx-auto"
              style={{ borderColor: THEME.primary, borderTopColor: 'transparent' }}
            ></div>
            <p className="mt-4 text-sm text-gray-600">Loading Promotion Data...</p>
          </div>
        </div>
      )}

      <div className="w-full space-y-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: THEME.primary }}>
            My Promotion Data Report
          </h1>
          <p className="mt-1 text-sm text-gray-500">Track your student promotion activities</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

          <div className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[180px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search student..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-9 pr-3 ${inputClass}`}
                />
              </div>

              <select
                value={dateFilter}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                className={inputClass}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="custom">Custom Date</option>
              </select>

              {dateFilter === 'custom' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleCustomDateChange}
                  className={inputClass}
                />
              )}

              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className={inputClass}
              >
                <option value="">All Status</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
              </select>

              <button
                onClick={clearFilters}
                style={{ backgroundColor: THEME.actionRed }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition"
              >
                <FiX size={14} className="shrink-0" />
                Clear
              </button>

              <div className="flex gap-2 ml-auto">
                <div className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold">
                  Delivered: <span className="font-bold">{deliveredCount}</span>
                </div>
                <div className="bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg text-xs font-semibold">
                  Read: <span className="font-bold">{readCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-3" style={{ backgroundColor: THEME.primary }}>
            <h2 className="text-white font-bold text-sm">Promotion Records</h2>
            <p className="text-white/80 text-xs">
              Total Records: {filteredPromotionData.length} • {getFilterDisplayText()}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>S.No</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Student Name</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Mobile Number</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Status</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Promotion Date</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ color: THEME.primary }}>Feedback</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-16 text-center">
                      <div
                        className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
                        style={{ backgroundColor: THEME.primaryLight, color: THEME.primary }}
                      >
                        <FiUsers className="text-2xl" />
                      </div>
                      <p className="text-lg font-bold" style={{ color: THEME.primary }}>
                        No data found for the selected filter
                      </p>
                      <p className="mt-1 text-sm text-gray-500">Try changing the date or status filter.</p>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((item, idx) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                      <td className="px-4 py-3 text-sm text-gray-600 font-medium">
                        {indexOfFirstItem + idx + 1}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-bold text-sm">
                              {item.student_name?.[0] || 'S'}
                            </span>
                          </div>
                          <span className="font-medium text-gray-800 truncate text-sm">
                            {item.student_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {item.mobile_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {renderStatusBadge(item.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(item.promotion_date)}
                      </td>
                      <td className="px-4 py-3">
                        {renderFeedbackCell(item)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredPromotionData.length > 0 && (
            <div className="px-6 py-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 text-sm">
                  Showing{' '}
                  <span className="font-semibold" style={{ color: THEME.primary }}>{indexOfFirstItem + 1}</span>
                  {' '}to{' '}
                  <span className="font-semibold" style={{ color: THEME.primary }}>{Math.min(indexOfLastItem, filteredPromotionData.length)}</span>
                  {' '}of{' '}
                  <span className="font-semibold" style={{ color: THEME.primary }}>{filteredPromotionData.length}</span>
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm font-semibold text-[#172C57] outline-none focus:border-[#172C57] focus:ring-2 focus:ring-[#172C57]"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={pageBtnClass}>First</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className={pageBtnClass}>Prev</button>
                <span
                  className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Page {currentPage} of {totalPages}
                </span>
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className={pageBtnClass}>Next</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={pageBtnClass}>Last</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Student Details Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto my-10 mt-[100px] overflow-hidden outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      >
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold" style={{ color: THEME.primary }}>Student Details</h2>
          <button
            onClick={closeModal}
            aria-label="Close modal"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-gray-500 transition hover:bg-gray-100"
          >
            &times;
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {selectedStudent && (
            <>
              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Student Name</p>
                <p className="font-bold text-lg" style={{ color: THEME.primary }}>{selectedStudent.student_name}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedStudent.mobile_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  {renderStatusBadge(selectedStudent.status)}
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Promotion Date</p>
                  <p className="font-semibold text-gray-800">{formatDate(selectedStudent.promotion_date)}</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Created At</p>
                <p className="font-medium text-gray-800">{formatDateTime(selectedStudent.created_date)}</p>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        isOpen={feedbackModalIsOpen}
        onRequestClose={closeFeedbackModal}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-auto my-10 mt-[100px] overflow-hidden outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      >
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold" style={{ color: THEME.primary }}>
                {selectedFeedbackStudent?.feedback ? 'Edit Feedback' : 'Add Feedback'}
              </h2>
              <p className="text-gray-500 mt-1 text-sm">
                {selectedFeedbackStudent?.student_name} • {selectedFeedbackStudent?.mobile_number}
              </p>
            </div>
            <button
              onClick={closeFeedbackModal}
              aria-label="Close modal"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-gray-500 transition hover:bg-gray-100"
            >
              &times;
            </button>
          </div>
        </div>

        <div className="p-5">
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Write your feedback here..."
            rows={6}
            className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 hover:border-gray-400 focus:border-[#172C57] focus:ring-2 focus:ring-[#172C57] resize-y min-h-[160px]"
          />
        </div>

        <div className="border-t border-slate-100 p-5 flex gap-3 justify-end bg-white">
          <button
            onClick={closeFeedbackModal}
            className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={updatePromotionFeedback}
            disabled={feedbackLoading || !feedbackText.trim()}
            style={{
              backgroundColor:
                feedbackLoading || !feedbackText.trim() ? THEME.disabledGray : THEME.primary,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {feedbackLoading ? 'Saving...' : 'Save Feedback'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeePromotionDataReport;